import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { monthInputToApiDate } from '@/lib/profile-dates';
import MonthYearSelect from '../MonthYearSelect';
import { EducationData } from './EducationTab';

const DEGREE_OPTIONS = [
  "Associate's", "Bachelor's", "Master's", "PhD", "MBA", "JD", "MD", "Certificate", "Diploma", "High School", "GED", "Other"
];

interface EducationFormProps {
  education: EducationData;
  isOtherDegree: boolean;
  isLoading: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDegreeChange: (value: string) => void;
  onResetDegreeDropdown: () => void;
  onIsOngoingChange: (checked: boolean) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

const EducationForm: React.FC<EducationFormProps> = ({
  education,
  isOtherDegree,
  isLoading,
  onChange,
  onDegreeChange,
  onResetDegreeDropdown,
  onIsOngoingChange,
  onSubmit,
  onCancel,
}) => (
  <div className="space-y-4 sm:space-y-6 mb-6 border-b pb-6">
    <h3 className="text-base sm:text-lg font-medium">Add New Education</h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor="institution">Institution</Label>
        <Input
          id="institution"
          name="institution"
          value={education.institution}
          onChange={onChange}
          placeholder="University or School Name"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="degree_type">Degree</Label>
        {isOtherDegree ? (
          <div className="flex gap-2">
            <Input
              id="degree_type"
              name="degree_type"
              value={education.degree_type}
              onChange={onChange}
              placeholder="Enter your degree"
              className="flex-1"
            />
            <Button
              variant="outline"
              size="icon"
              onClick={onResetDegreeDropdown}
              type="button"
              className="h-10 w-10"
            >
              ×
            </Button>
          </div>
        ) : (
          <Select onValueChange={onDegreeChange}>
            <SelectTrigger>
              <SelectValue placeholder="Select degree" />
            </SelectTrigger>
            <SelectContent>
              {DEGREE_OPTIONS.map(degree => (
                <SelectItem key={degree} value={degree}>{degree}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
    </div>
    <div className="space-y-2">
      <Label htmlFor="major">Field of Study</Label>
      <Input
        id="major"
        name="major"
        value={education.major}
        onChange={onChange}
        placeholder="e.g., Business Administration"
      />
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor="start_date">Start Month</Label>
        <MonthYearSelect
          value={education.start_date}
          onChange={(value) => onChange({
            target: { name: 'start_date', value: monthInputToApiDate(value) },
          } as React.ChangeEvent<HTMLInputElement>)}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="end_date">End Month (or Expected)</Label>
        <MonthYearSelect
          value={education.end_date}
          onChange={(value) => onChange({
            target: { name: 'end_date', value: monthInputToApiDate(value) },
          } as React.ChangeEvent<HTMLInputElement>)}
          disabled={education.is_ongoing}
        />
      </div>
    </div>
    <div className="flex items-center space-x-2">
      <input
        type="checkbox"
        id="is_ongoing"
        name="is_ongoing"
        checked={education.is_ongoing}
        onChange={e => onIsOngoingChange(e.target.checked)}
        className="h-4 w-4 rounded border-gray-300 text-interview-blue focus:ring-interview-blue"
      />
      <Label htmlFor="is_ongoing" className="text-sm font-medium">
        Currently studying here
      </Label>
    </div>
    <div className="mt-6 flex flex-col sm:flex-row justify-end gap-2">
      <Button 
        variant="outline" 
        onClick={onCancel} 
        disabled={isLoading}
        className="w-full sm:w-auto order-2 sm:order-1"
      >
        Cancel
      </Button>
      <Button
        variant="default"
        onClick={onSubmit}
        disabled={isLoading || !education.institution || !education.degree_type || !education.major}
        className="w-full sm:w-auto order-1 sm:order-2"
      >
        {isLoading ? 'Saving...' : 'Save'}
      </Button>
    </div>
  </div>
);

export default EducationForm;
